import React, { useCallback, useEffect, type FC, type ReactNode } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { motion, AnimatePresence as MotionPresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';
import QrCodeOutlinedIcon from '@mui/icons-material/QrCodeOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import CenterFocusStrongOutlinedIcon from '@mui/icons-material/CenterFocusStrongOutlined';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useOverlayContext } from '@providers/OverlayProvider';
import { isMapFeatureFlagEnabled } from '@helpers/events';
import OVERLAYS from '@constants/overlays';

import { EditorNodeKind, getSpotType, SpotType } from '../editorCategories';
import { useEditorMode } from '../EditorModeContext';
import { useEditorStyles } from '../styles';
import MapColorsInspector from './MapColorsInspector';
import SpotInspector from './SpotInspector';
import AIScriptInspector from './AIScriptInspector';

const AnimatePresence = MotionPresence as FC<{ children: ReactNode }>;

const getSpotTypeIcon = (type: SpotType, sx: object) => {
  if (type === 'VIDEO') {
    return <PlayArrowOutlinedIcon sx={sx} />;
  }
  if (type === 'QR') {
    return <QrCodeOutlinedIcon sx={sx} />;
  }
  return <ImageOutlinedIcon sx={sx} />;
};

const normalizeUeIdentifier = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const Inspector = () => {
  const { t: translate } = useTranslation();
  const styles = useEditorStyles();
  const { selectedNode, clearSelection } = useEditorMode();
  const {
    roomSpots,
    brandingSpots,
    currentRoom,
    currentEvent,
    currentBrandingId,
    isBrandingModeEnabled,
    selectedCustomizationTargetSize,
    setSelectedCustomizationUeId,
    setIsEditorInspectorOpen,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const { isOverlayCurrentlyOpen } = useOverlayContext();

  // Notify the rest of the app (specifically the Assistant wrapper) when the
  // inspector is visible so Noa hides itself for the duration. Cleanup restores
  // the assistant when EditorMode unmounts.
  const isInspectorVisible = !!selectedNode;
  useEffect(() => {
    setIsEditorInspectorOpen(isInspectorVisible);
    return () => {
      setIsEditorInspectorOpen(false);
    };
  }, [isInspectorVisible, setIsEditorInspectorOpen]);

  const isBlockingOverlayOpen =
    isOverlayCurrentlyOpen(OVERLAYS.ADMIN_TOOLS) ||
    isOverlayCurrentlyOpen(OVERLAYS.PROFILE);
  useEffect(() => {
    if (isBlockingOverlayOpen && selectedNode) {
      clearSelection();
      setSelectedCustomizationUeId(null);
    }
  }, [
    isBlockingOverlayOpen,
    selectedNode,
    clearSelection,
    setSelectedCustomizationUeId,
  ]);

  const handleCloseInspector = () => {
    clearSelection();
    setSelectedCustomizationUeId(null);
  };

  const spotUeId =
    selectedNode?.kind === EditorNodeKind.SPOT
      ? selectedNode.ueIdentifier
      : null;
  const mapId = currentRoom?.map?.mapId;
  const canTeleportToSpot = isMapFeatureFlagEnabled(
    currentEvent,
    mapId,
    'TELEPORT_UE_ACTION',
  );
  const canBlinkSpot = isMapFeatureFlagEnabled(
    currentEvent,
    mapId,
    'RUNTIME_SPOT_UPDATE_UE',
  );

  const handleTeleportToSpot = useCallback(() => {
    if (!spotUeId) {
      return;
    }
    sendMessageToUnreal({
      action: 'TELEPORT_TO_SPOT',
      brandingSpotId: spotUeId,
    });
  }, [spotUeId, sendMessageToUnreal]);

  const handleBlinkSpot = useCallback(() => {
    if (!spotUeId) {
      return;
    }
    sendMessageToUnreal({
      action: 'BLINK_SPOT',
      brandingSpotId: spotUeId,
    });
  }, [spotUeId, sendMessageToUnreal]);

  let titleIcon: React.ReactNode = null;
  let titleText = '';
  let subtitleText: string | null = null;
  let body: React.ReactNode = null;

  if (selectedNode?.kind === EditorNodeKind.MAP_AND_COLORS) {
    titleIcon = <PaletteOutlinedIcon sx={styles.panelTitleIcon} />;
    titleText = translate('editorMode.mapAndColors', 'Map and colors');
    body = <MapColorsInspector />;
  } else if (selectedNode?.kind === EditorNodeKind.SPOT) {
    const ueId = selectedNode.ueIdentifier;
    const source =
      currentBrandingId && isBrandingModeEnabled ? brandingSpots : roomSpots;
    const selectedUeId = normalizeUeIdentifier(ueId);
    const spot = Array.isArray(source)
      ? (source as Array<Record<string, unknown>>).find(
          (s) =>
            typeof (s as { ueIdentifier?: unknown }).ueIdentifier ===
              'string' &&
            normalizeUeIdentifier(
              (s as { ueIdentifier: string }).ueIdentifier,
            ) === selectedUeId,
        )
      : null;
    const type = getSpotType(spot ?? {});
    const name =
      typeof (spot as { name?: unknown } | null)?.name === 'string'
        ? ((spot as { name: string }).name as string)
        : ueId;
    titleIcon = getSpotTypeIcon(type, styles.panelTitleIcon);
    titleText = name;
    subtitleText = ueId;
    body = <SpotInspector key={ueId} ueIdentifier={ueId} />;
  }

  // Must run before any early return — React requires the hooks order to be
  // stable across renders. REGION_SCRIPT switches to a different render branch
  // below, so this useCallback has to live above that branch as well.
  const handleCopySubtitle = useCallback(async () => {
    if (!subtitleText) {
      return;
    }
    try {
      await navigator.clipboard.writeText(subtitleText);
    } catch (copyError) {
      console.warn(
        `Failed to copy identifier: ${(copyError as Error)?.message}`,
        { ueIdentifier: subtitleText },
      );
    }
  }, [subtitleText]);

  const spotDimensionsLabel =
    selectedNode?.kind === EditorNodeKind.SPOT &&
    selectedCustomizationTargetSize &&
    selectedCustomizationTargetSize.width > 0 &&
    selectedCustomizationTargetSize.height > 0
      ? `${selectedCustomizationTargetSize.width} × ${selectedCustomizationTargetSize.height}`
      : null;

  // REGION_SCRIPT renders its own glass panel + header (direction B design),
  // so it bypasses the shared chrome below.
  if (selectedNode?.kind === EditorNodeKind.REGION_SCRIPT) {
    return (
      <AnimatePresence>
        <Box key="inspector-ai" sx={styles.inspectorWrap}>
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 16 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            style={{ width: '100%' }}
          >
            <AIScriptInspector
              key={`script-${selectedNode.scriptId ?? selectedNode.trigger}`}
              trigger={selectedNode.trigger}
              scriptId={selectedNode.scriptId}
            />
          </motion.div>
        </Box>
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {selectedNode && (
        <Box key="inspector" sx={styles.inspectorWrap}>
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 16 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            style={{ width: '100%' }}
          >
            <Box
              sx={[
                styles.glassPanel,
                {
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: 'calc(100vh - 208px)',
                  overflow: 'hidden',
                },
              ]}
            >
              <Box sx={styles.panelHeader}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Box sx={[styles.panelTitleRow, { minWidth: 0 }]}>
                    {titleIcon}
                    <Tooltip
                      title={titleText}
                      placement="bottom"
                      enterDelay={400}
                    >
                      <Typography
                        sx={[
                          styles.panelTitle,
                          {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                          },
                        ]}
                      >
                        {titleText}
                      </Typography>
                    </Tooltip>
                  </Box>
                  {subtitleText && (
                    <Box sx={styles.panelSubtitleRow}>
                      <Tooltip
                        title={subtitleText}
                        placement="bottom"
                        enterDelay={400}
                      >
                        <Typography sx={styles.panelSubtitle}>
                          {subtitleText}
                        </Typography>
                      </Tooltip>
                      <IconButton
                        onClick={handleCopySubtitle}
                        sx={styles.panelSubtitleCopy}
                        size="small"
                        aria-label={translate(
                          'editorMode.copyIdentifier',
                          'Copy identifier',
                        )}
                      >
                        <ContentCopyOutlinedIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                      {spotDimensionsLabel && (
                        <Tooltip
                          title={translate(
                            'editorMode.surfaceSize',
                            'Surface size',
                          )}
                          placement="bottom"
                          enterDelay={400}
                        >
                          <Typography
                            component="span"
                            sx={styles.panelSubtitleSize}
                          >
                            {spotDimensionsLabel}
                          </Typography>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Box>
                {spotUeId && canTeleportToSpot && (
                  <Tooltip
                    title={translate(
                      'editorMode.teleportToSpot',
                      'Teleport to spot',
                    )}
                    placement="bottom"
                    enterDelay={400}
                  >
                    <IconButton
                      onClick={handleTeleportToSpot}
                      sx={styles.panelIconButton}
                      size="small"
                      aria-label={translate(
                        'editorMode.teleportToSpot',
                        'Teleport to spot',
                      )}
                    >
                      <MyLocationOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {spotUeId && canBlinkSpot && (
                  <Tooltip
                    title={translate(
                      'editorMode.highlightSpot',
                      'Highlight spot',
                    )}
                    placement="bottom"
                    enterDelay={400}
                  >
                    <IconButton
                      onClick={handleBlinkSpot}
                      sx={styles.panelIconButton}
                      size="small"
                      aria-label={translate(
                        'editorMode.highlightSpot',
                        'Highlight spot',
                      )}
                    >
                      <CenterFocusStrongOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton
                  onClick={handleCloseInspector}
                  sx={styles.panelIconButton}
                  size="small"
                  aria-label={translate('editorMode.close', 'Close')}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box sx={styles.panelBody}>{body}</Box>
            </Box>
          </motion.div>
        </Box>
      )}
    </AnimatePresence>
  );
};

export default Inspector;
